/* eslint-disable no-console */
import { useCallback, useEffect, useRef } from 'react';

import { HttpTransportType, HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import * as Sentry from '@sentry/react';
import { RipErrorMessage, RipFilesReady, RipProgressUpdate, useSignalRState } from 'contexts/SignalRContext';
import { debugError, debugLog } from 'utils/logger';

type Timestamp = string | number | Date;

type SignalRClientApi = {
   startAsync: () => Promise<void>;
   stopAsync: () => Promise<void>;
   pingAsync: () => Promise<string | null>;
   unregisterFromRipUpdates: () => Promise<void>;
   registerForRipUpdates: () => Promise<void>;
};

export const useSignalRClient = (serverUrl: string, clientId: string): SignalRClientApi => {
   const connectionRef = useRef<HubConnection | null>(null);
   const stoppedForRegistrationErrorRef = useRef(false);
   const { updateState } = useSignalRState();

   const handleProgressUpdate = useCallback(
      (progressData: unknown) => {
         const payload = progressData as Partial<RipProgressUpdate> | null;

         if (
            !payload ||
            typeof payload.clientId !== 'string' ||
            typeof payload.message !== 'string' ||
            typeof payload.progressPercentage !== 'number'
         ) {
            debugError('[Error] Failed to parse progress update: Invalid payload');
            return;
         } else {
            //debugLog(`[SignalR] [timestamp] ${payload.timestamp} [ProgressUpdate] progress%: ${payload.progressPercentage}; message: ${payload.message}`);
            console.log(
               `[SignalR] timestamp: ${payload.timestamp} progress%: ${payload.progressPercentage}; message: ${payload.message}`,
            );
         }

         // Update global state
         updateState({
            progress: {
               clientId: payload.clientId,
               message: payload.message,
               progressPercentage: payload.progressPercentage,
               timestamp: payload.timestamp || new Date().toISOString(),
            },
         });
      },
      [updateState],
   );

   const handleFilesReady = useCallback(
      (fileData: unknown) => {
         const payload = fileData as Partial<RipFilesReady> | null;

         if (
            !payload ||
            typeof payload.clientId !== 'string' ||
            !Array.isArray(payload.scodixFileData) ||
            typeof payload.uriCMYK !== 'string'
         ) {
            debugError('[Error] Failed to parse file created message: Invalid payload');
            return;
         }

         // Normalize layer data - handle both lowercase and uppercase property names
         const normalizedScodixFileData = payload.scodixFileData.map((item: any) => {
            // Debug: log the raw item to see its structure
            if (process.env.NODE_ENV === 'development') {
               console.log('[SignalR] Raw layer item:', item);
            }
            return {
               Layer: item.Layer || item.layer || '',
               Uri: item.Uri || item.uri || '',
            };
         });

         debugLog(
            `[SignalR] [FilesReady] uriCMYK: ${payload.uriCMYK}; ` +
               `layers: ${normalizedScodixFileData.map((x) => x.Layer).join(', ')}`,
         );

         // Update global state and clear progress
         updateState({
            progress: null, // Clear progress when files are ready
            filesReady: {
               clientId: payload.clientId,
               scodixFileData: normalizedScodixFileData,
               uriCMYK: payload.uriCMYK,
               timestamp: payload.timestamp || new Date().toISOString(),
            },
         });
      },
      [updateState],
   );

   const handleError = useCallback(
      (errorData: unknown) => {
         const payload = errorData as Record<string, unknown> | null;
         if (!payload || typeof payload !== 'object') {
            // eslint-disable-next-line no-console
            console.warn('[SignalR] Error event received with invalid payload (null or non-object)');
            return;
         }

         // Accept camelCase or PascalCase (server may send ClientId, Error, Message)
         const clientId = (payload.clientId ?? payload.ClientId) as string | undefined;
         const errorMsg =
            (payload.error ?? payload.Error ?? payload.message ?? payload.Message) as string | undefined;

         if (typeof errorMsg !== 'string') {
            // eslint-disable-next-line no-console
            console.warn('[SignalR] Error event received with invalid payload (missing error string):', payload);
            return;
         }

         debugLog(`[SignalR] [Error] clientId: ${clientId ?? 'n/a'}; error: ${errorMsg}`);

         // Duplicate registration: use webapp message, not API message
         if (errorMsg.includes('Already registered for RIP')) {
            updateState({
               registrationError: 'Account already connected from another device.',
               error: null,
            });
            return;
         }

         // Update global state for other errors
         updateState({
            error: {
               clientId: typeof clientId === 'string' ? clientId : '',
               error: errorMsg,
               timestamp: (payload.timestamp as string) || new Date().toISOString(),
            },
         });
      },
      [updateState],
   );

   const handleConnected = useCallback(
      (message: string) => {
         debugLog(`[SignalR] ${message}`);
         updateState({
            isConnected: true,
            connectionError: null,
         });
      },
      [updateState],
   );

   const handleRegistered = useCallback((data: unknown) => {
      const payload = data as { message?: string; Message?: string; clientId?: string };
      const msg = payload?.message ?? payload?.Message ?? `Registered for RIP: ${clientId}`;
      debugLog(`[SignalR] ${msg}`);
   }, [clientId]);

   // Register for RIP updates. Server returns true on success, false on duplicate.
   const registerForRipUpdates = useCallback(async (): Promise<void> => {
      const conn = connectionRef.current;
      if (!conn) return;

      const success = await conn.invoke<boolean>('RegisterForRipUpdates', clientId);
      if (success) {
         debugLog(`[SignalR] Registered for RIP: ${clientId}`);
         updateState({ registrationError: null });
      } else {
         debugError('[SignalR] Registration failed (e.g. duplicate)');
         throw new Error('Account already connected from another device.');
      }
   }, [clientId, updateState]);

   // Start the connection
   const startAsync = useCallback(async () => {
      const hubUrl = `${serverUrl}/ripHub`;

      debugLog('Starting SignalR Client...');
      debugLog(`HubUrl: ${hubUrl}`);
      debugLog(`ClientId: ${clientId}`);
      debugLog('----------------------------------------');

      const newConnection = new HubConnectionBuilder()
         .withUrl(hubUrl, {
            skipNegotiation: true,
            transport: HttpTransportType.WebSockets,
         })
         .withAutomaticReconnect()
         .configureLogging(LogLevel.Debug)
         .build();

      // Set up event handlers
      newConnection.on('Connected', handleConnected);
      newConnection.on('Registered', handleRegistered);
      newConnection.on('ProgressUpdate', handleProgressUpdate);
      newConnection.on('FilesReady', handleFilesReady);
      newConnection.on('Error', handleError);

      newConnection.onreconnecting((error) => {
         debugLog(`[SignalR] Reconnecting: ${error?.message}`);
         updateState({
            isConnected: false,
            connectionError: 'Connection lost. Attempting to reconnect...',
         });
      });

      newConnection.onreconnected((newConnectionId) => {
         debugLog(`[SignalR] Reconnected. Connection ID: ${newConnectionId}`);
         updateState({
            isConnected: true,
            connectionError: null,
         });
         void registerForRipUpdates();
      });

      newConnection.onclose((error) => {
         debugLog(`[SignalR] Connection closed: ${error?.message}`);
         if (stoppedForRegistrationErrorRef.current) {
            stoppedForRegistrationErrorRef.current = false;
            return;
         }
         const errorMessage = error
            ? `Connection closed: ${error.message}`
            : 'Connection to server lost. Please check your network connection.';
         updateState({
            isConnected: false,
            connectionError: errorMessage,
         });
      });

      try {
         await newConnection.start();
         debugLog(`[SignalR] Connected to ${hubUrl}`);
         connectionRef.current = newConnection;
         updateState({
            isConnected: true,
            connectionError: null,
            registrationError: null,
         });

         // Register for specific RIP updates
         await registerForRipUpdates();
      } catch (error) {
         debugError('[SignalR] Connection failed', error);
         // if (process.env.NODE_ENV !== 'development') {
         //Sentry.captureException(error);
         // }
         const isRegistrationError = error instanceof Error && error.message === 'Account already connected from another device.';
         if (isRegistrationError) {
            stoppedForRegistrationErrorRef.current = true;
            void connectionRef.current?.stop();
            connectionRef.current = null;
            updateState({
               isConnected: false,
               connectionError: null,
               registrationError: 'Account already connected from another device.',
            });
         } else {
            const errorMessage =
               error instanceof Error
                  ? `Failed to connect: ${error.message}`
                  : 'Failed to connect to server. Please check your network connection.';
            updateState({
               isConnected: false,
               connectionError: errorMessage,
               registrationError: null,
            });
         }
      }
   }, [
      serverUrl,
      clientId,
      handleConnected,
      handleRegistered,
      handleProgressUpdate,
      handleFilesReady,
      handleError,
      registerForRipUpdates,
      updateState,
   ]);

   // Stop the connection
   const stopAsync = useCallback(async () => {
      if (connectionRef.current) {
         await connectionRef.current.stop();
         connectionRef.current = null;
         updateState({
            isConnected: false,
            progress: null,
            filesReady: null,
            error: null,
            connectionError: null,
            registrationError: null,
         });
         debugLog('Client stopped.');
      }
   }, [updateState]);

   // Ping server
   const pingAsync = useCallback(async () => {
      if (!connectionRef.current) {
         debugLog('[SignalR] No connection available');
         return null;
      }

      try {
         return (await connectionRef.current.invoke('Ping')) as string;
      } catch (error) {
         debugError('[SignalR] Ping failed', error);
         Sentry.captureException(error);
         return null;
      }
   }, []);

   // Unregister from RIP updates
   const unregisterFromRipUpdates = useCallback(async () => {
      if (!connectionRef.current) return;

      try {
         await connectionRef.current.invoke('UnregisterFromRipUpdates', clientId);
         debugLog(`[SignalR] Unregistered from RIP: ${clientId}`);
      } catch (error) {
         debugError('[SignalR] Failed to unregister from RIP', error);
         // if (process.env.NODE_ENV !== 'development') {
         Sentry.captureException(error);
         // }
      }
   }, [clientId]);

   // Cleanup on unmount
   useEffect(() => {
      return () => {
         if (connectionRef.current) {
            void stopAsync();
         }
      };
   }, [stopAsync]);

   return {
      startAsync,
      stopAsync,
      pingAsync,
      unregisterFromRipUpdates,
      registerForRipUpdates,
   };
};
